// UTM params tag traffic arriving via a shared deployed-app link. Kept in sync
// with DEPLOYMENT_UTM in apps/runtime/src/tools/deploy.ts (separate package, no
// shared import). The runtime bakes these into the URL it reports and stores in
// the deployments desk item; the web app appends them to links it builds itself.
export const DEPLOYED_APP_UTM = "utm_source=squidler&utm_medium=agent&utm_campaign=deployed_app";

/**
 * Append the deployed-app UTM params to a URL or path. Idempotent — a no-op if
 * the URL already carries utm_source (so re-wrapping a runtime-built URL or a
 * legacy desk item is safe). Handles relative paths and `#` fragments.
 */
export function withDeployedAppUtm(url: string): string {
  if (/[?&]utm_source=/.test(url)) return url;
  const hashIndex = url.indexOf("#");
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? "" : url.slice(hashIndex);
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}${DEPLOYED_APP_UTM}${fragment}`;
}

/**
 * Out-of-PWA-scope link host for deployed apps (app.squidler.io — see
 * deploy/nginx/app.squidler.io). The PWA's manifest scope covers all of
 * squidler.io, so a same-origin /apps/… link opens INSIDE the installed PWA on
 * mobile — no browser UI, chat screen gone. Links on this cross-origin host
 * open in a browser context (Android custom tab / iOS sheet) and 302 back to
 * the apex.
 *
 * Read at RUNTIME on the server (never NEXT_PUBLIC — the web artifact is
 * built once and promoted dev→prod, see scripts/assert-web-env-agnostic.sh)
 * and handed to client components via AppLinkOriginProvider (root layout).
 * Empty (local dev, dev box) disables the rewrite. Kept in sync with
 * DEPLOYED_APP_LINK_ORIGIN in apps/runtime/src/tools/deploy.ts (separate
 * package, no shared import).
 */
export function deployedAppLinkOrigin(): string {
  return (process.env.DEPLOYED_APP_LINK_ORIGIN ?? "").replace(/\/+$/, "");
}

/**
 * The site's own origin, from the same APP_BASE_URL the boot contract
 * requires on the boxes (lib/boot-env.ts); the local default is the dev web
 * port. Handed to client components through AppLinkOriginProvider, which is
 * how the chat tells a link to one of our pages from an outbound one.
 */
export function siteOrigin(): string {
  return (process.env.APP_BASE_URL ?? "http://localhost:3100").replace(/\/+$/, "");
}

const LIVE_APP_URL = /^(?:https?:\/\/[^/]+)?\/apps\/([^/?#]+)\/([^/?#]+)(\/[^?#]*)?([?#].*)?$/;

/** Is this a live-app URL or path (`/apps/{username}/{slug}/…`)? The in-product
 *  dashboard (`/a/{slug}/apps/…`) is not. */
export const isLiveAppLink = (url: string): boolean => LIVE_APP_URL.test(url);

/**
 * Rewrite a live-app URL or path (`/apps/{username}/{slug}/…` on any origin)
 * to the out-of-scope link host: `{origin}/{username}/{slug}/…`. Query and
 * fragment survive. Non-app URLs — and everything when the origin is empty —
 * pass through unchanged, so legacy stored URLs and dev environments keep
 * today's behavior. Pure: server callers pass `deployedAppLinkOrigin()`,
 * client components the `useAppLinkOrigin()` context value.
 */
export function toDeployedAppLink(url: string, origin: string): string {
  if (!origin) return url;
  const match = LIVE_APP_URL.exec(url);
  if (!match) return url;
  const [, username, slug, rest, suffix] = match;
  return `${origin.replace(/\/+$/, "")}/${username}/${slug}${rest ?? "/"}${suffix ?? ""}`;
}
